/* eslint-disable */
import React, { useState, useEffect } from 'react'
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    MobileStepper,
    Box,
    IconButton,
    useTheme,
    useMediaQuery,
} from '@mui/material'
import {
    KeyboardArrowLeft,
    KeyboardArrowRight,
    Close,
} from '@mui/icons-material'

const tutorialSteps = [
    {
        label: 'Platform Walkthrough',
        description:
            'Watch a quick walkthrough of our project to get started and see all the features.',
        videoUrl: 'https://www.youtube.com/embed/4TBNINt3oTs?autoplay=1&mute=1',
    },
    {
        label: 'Dashboard Overview',
        description:
            'Welcome to your personalized Dashboard! Quickly access your courses, grades, notes, and upcoming deadlines all in one place.',
        imgPath: '/images/tutorial/dashboard.png',
    },
    {
        label: 'Academic Calendar',
        description:
            'Keep track of all your course schedules, assignment deadlines, and quiz due dates. You can even sync events with your local calendar.',
        imgPath: '/images/tutorial/calendar.png',
    },
    {
        label: 'Advanced Quizzes',
        description:
            'Experience a powerful quiz engine with support for strict time limits, graded modes, and low-stakes practice sessions.',
        imgPath: '/images/tutorial/quiz.png',
    },
    {
        label: 'Grade Analyzer',
        description:
            'Set target grades, import from your gradebook, and calculate exactly what you need on upcoming assignments to achieve your academic goals.',
        imgPath: '/images/tutorial/gradeanalyzer.png',
    },
    {
        label: 'Rich Text Notes',
        description:
            'Take detailed, rich-text notes for your courses. Organize them with tags, format your text, and seamlessly auto-save drafts.',
        imgPath: '/images/tutorial/notes.png',
    },
    {
        label: 'Live Meetings Integration',
        description:
            'Seamlessly schedule and join live sessions with integrated Google Meet support directly from your course announcements.',
        imgPath: '/images/tutorial/googlemeet.png',
    },
    {
        label: 'Profile & Career Opportunities',
        description:
            'Customize your public profile, showcase your education, and explore tailored career opportunities recommended just for you.',
        imgPath: '/images/tutorial/profile.png',
    },
]

function TutorialModal() {
    const theme = useTheme()
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
    const [open, setOpen] = useState(false)
    const [activeStep, setActiveStep] = useState(0)
    const [imageError, setImageError] = useState(false)
    const maxSteps = tutorialSteps.length

    useEffect(() => {
        // Check if the user has already seen the tutorial
        const hasSeenTutorial = localStorage.getItem('hasSeenTutorial')
        if (!hasSeenTutorial) {
            // Slight delay so the user sees the page first before the modal pops up
            const timer = setTimeout(() => {
                setOpen(true)
            }, 1000)
            return () => clearTimeout(timer)
        }
    }, [])

    useEffect(() => {
        const handleShowTutorial = () => {
            setActiveStep(0)
            setOpen(true)
        }
        window.addEventListener('show-tutorial', handleShowTutorial)
        return () =>
            window.removeEventListener('show-tutorial', handleShowTutorial)
    }, [])

    useEffect(() => {
        setImageError(false)
    }, [activeStep])

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1)
    }

    const handleClose = () => {
        localStorage.setItem('hasSeenTutorial', 'true')
        setOpen(false)
    }

    return (
        <Dialog
            fullScreen={fullScreen}
            open={open}
            onClose={handleClose}
            aria-labelledby="tutorial-dialog-title"
            PaperProps={{
                sx: {
                    borderRadius: fullScreen ? 0 : '16px',
                    maxWidth: '800px',
                    width: '100%',
                    overflow: 'hidden',
                },
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    bgcolor: 'var(--color-primary)',
                    color: '#ffffff',
                    p: 3,
                    textAlign: 'center',
                }}
            >
                <Typography
                    id="tutorial-dialog-title"
                    variant="h5"
                    fontWeight="bold"
                    sx={{ color: '#ffffff !important' }}
                >
                    {tutorialSteps[activeStep].label}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <Close sx={{ color: '#ffffff !important' }} />
                </IconButton>
            </Box>

            <DialogContent
                sx={{
                    p: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        height: { xs: 250, sm: 350, md: 450 },
                        bgcolor: 'var(--color-surface-2)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    {imageError ? (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                color: 'var(--color-text-muted)',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: '3rem',
                                    marginBottom: '10px',
                                }}
                            >
                                📸
                            </div>
                            <div style={{ fontWeight: 'bold' }}>
                                Screenshot Placeholder
                            </div>
                            <div
                                style={{ fontSize: '0.8rem', marginTop: '5px' }}
                            >
                                Please add screenshot to{' '}
                                {tutorialSteps[activeStep].imgPath}
                            </div>
                        </div>
                    ) : tutorialSteps[activeStep].videoUrl ? (
                        <Box
                            component="iframe"
                            src={tutorialSteps[activeStep].videoUrl}
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            sx={{ width: '100%', height: '100%' }}
                        />
                    ) : (
                        <Box
                            component="img"
                            src={tutorialSteps[activeStep].imgPath}
                            alt={tutorialSteps[activeStep].label}
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                            }}
                            onError={() => setImageError(true)}
                        />
                    )}
                </Box>
                <Box sx={{ p: 4, textAlign: 'center', minHeight: '120px' }}>
                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: '1.1rem',
                            color: 'var(--color-text)',
                            lineHeight: 1.6,
                        }}
                    >
                        {tutorialSteps[activeStep].description}
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    justifyContent: 'space-between',
                    p: 3,
                    borderTop: '1px solid var(--color-border)',
                }}
            >
                <Button
                    size="large"
                    onClick={handleBack}
                    disabled={activeStep === 0}
                    startIcon={<KeyboardArrowLeft />}
                    sx={{ fontWeight: 'bold' }}
                >
                    Back
                </Button>

                <MobileStepper
                    steps={maxSteps}
                    position="static"
                    activeStep={activeStep}
                    sx={{
                        bgcolor: 'transparent',
                        flexGrow: 1,
                        justifyContent: 'center',
                        '& .MuiMobileStepper-dotActive': {
                            backgroundColor: 'var(--color-accent)',
                            transform: 'scale(1.3)',
                        },
                    }}
                    nextButton={null}
                    backButton={null}
                />

                {activeStep === maxSteps - 1 ? (
                    <Button
                        size="large"
                        variant="contained"
                        onClick={handleClose}
                        sx={{
                            bgcolor: 'var(--color-primary)',
                            '&:hover': {
                                bgcolor: 'var(--color-primary-hover)',
                            },
                            fontWeight: 'bold',
                            px: 4,
                        }}
                    >
                        Get Started
                    </Button>
                ) : (
                    <Button
                        size="large"
                        variant="contained"
                        onClick={handleNext}
                        endIcon={<KeyboardArrowRight />}
                        sx={{
                            bgcolor: 'var(--color-primary)',
                            '&:hover': {
                                bgcolor: 'var(--color-primary-hover)',
                            },
                            fontWeight: 'bold',
                        }}
                    >
                        Next
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default TutorialModal
